import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { sponsorsApi, type Sponsor } from "@/integrations/mysql/client";
import { usePageContent } from "@/hooks/usePageContent";

export function SponsorsCarousel() {
  const { data: sponsors = [], isLoading } = useQuery({
    queryKey: ["sponsors-public"],
    queryFn: () => sponsorsApi.list(true),
    staleTime: 60_000,
  });
  const { getText } = usePageContent('home');
  const sectionLabel = getText('sponsors', 'label', 'Parceiros & Patrocinadores');

  const trackRef = useRef<HTMLDivElement>(null);
  const [paused, setPaused] = useState(false);
  const posRef = useRef(0);
  const rafRef = useRef<number | null>(null);

  // Duplicate list for seamless infinite scroll
  const items = sponsors.length > 0 ? [...sponsors, ...sponsors] : [];

  useEffect(() => {
    const el = trackRef.current;
    if (!el || sponsors.length === 0) return;

    const speed = 0.6; // px per frame — positive = left→right

    const animate = () => {
      if (!paused) {
        posRef.current += speed;
        // Reset when we've scrolled one full half (seamless loop)
        const half = el.scrollWidth / 2;
        if (posRef.current >= half) posRef.current = 0;
        // Translate RIGHT: start at -half, move toward 0 and cycle
        el.style.transform = `translateX(${posRef.current - half}px)`;
      }
      rafRef.current = requestAnimationFrame(animate);
    };

    // Initialise so first frame starts off-screen to the left
    posRef.current = 0;
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [sponsors, paused]);

  if (isLoading || sponsors.length === 0) return null;

  return (
    <section className="py-10 border-y border-gray-100 bg-white overflow-hidden">
      <div className="container mx-auto px-6 mb-6">
        <p className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-400 text-center">
          {sectionLabel}
        </p>
      </div>

      <div
        className="overflow-hidden"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <div ref={trackRef} className="flex gap-6 w-max will-change-transform">
          {items.map((sponsor, i) => (
            <SponsorCard key={`${sponsor.id}-${i}`} sponsor={sponsor} />
          ))}
        </div>
      </div>
    </section>
  );
}

function SponsorCard({ sponsor }: { sponsor: Sponsor }) {
  const inner = (
    <div className="flex flex-col items-center justify-center gap-2.5 w-40 h-28 px-4 py-4 bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-md hover:border-brand-primary/20 transition-all duration-200 cursor-pointer shrink-0">
      {sponsor.logo_url ? (
        <img
          src={sponsor.logo_url}
          alt={sponsor.name}
          className="max-h-12 max-w-[120px] object-contain opacity-70 hover:opacity-100 transition-opacity"
          draggable={false}
        />
      ) : (
        <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center text-xl select-none">
          🤝
        </div>
      )}
      <p className="text-xs text-gray-500 font-medium text-center leading-tight line-clamp-2">
        {sponsor.name}
      </p>
    </div>
  );

  if (sponsor.link_url) {
    return (
      <a href={sponsor.link_url} target="_blank" rel="noopener noreferrer">
        {inner}
      </a>
    );
  }
  return inner;
}
